import { type SupplierJoinRequest } from '@choco-admin/types';
import { getInitials } from '@choco-admin/utils';
import {
  Avatar,
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import React from 'react';
import { FiMail, FiPhone, FiUserPlus } from 'react-icons/fi';

import { AlertCard } from '../../../../../../components/AlertCard';

import { useSupplierJoinRequests } from './hooks/useSupplierJoinRequests';
import { useUpdateSupplierJoinRequest } from './hooks/useUpdateSupplierJoinRequest';

interface SupplierJoinRequestsProps {
  supplierId: string;
  /** Refreshes the member list once an approval adds someone to the team. */
  onTeamChanged?: () => Promise<unknown>;
}

const displayName = (request: SupplierJoinRequest): string =>
  request.user?.name?.trim() || 'Unnamed user';

export const SupplierJoinRequests: React.FC<SupplierJoinRequestsProps> = ({
  supplierId,
  onTeamChanged,
}) => {
  const { requests, isTruncated, isLoading, errorMessage, refetch } =
    useSupplierJoinRequests(supplierId);

  const { decideJoinRequest, processingRequestId } =
    useUpdateSupplierJoinRequest({
      // Both lists are refreshed, and neither depends on the other. Awaiting
      // them in sequence meant a failed request refetch stopped the member list
      // refreshing, so an approved person was missing from Team Members with a
      // success toast on screen.
      onRefetch: () =>
        Promise.allSettled([refetch(), onTeamChanged?.() ?? Promise.resolve()]),
    });

  const renderBody = (): React.ReactNode => {
    if (isLoading && requests.length === 0) {
      return (
        <div className="flex justify-center items-center py-8">
          <Spinner size="lg" />
        </div>
      );
    }

    // Only blank the list when there is nothing to show — a failed background
    // refetch should not hide requests already on screen.
    if (errorMessage && requests.length === 0) {
      return (
        <AlertCard
          type="error"
          title="Requests to join unavailable"
          message={errorMessage}
        />
      );
    }

    if (requests.length === 0) {
      return (
        <div className="text-center py-8 text-default-500">
          <FiUserPlus size={48} className="mx-auto mb-4 opacity-50" />
          {/* The backend returns at most 20 requests of any status, in index
              order rather than by date, so "most recent" would be wrong too.
              With a full window this screen cannot see past it and must not
              claim nobody is waiting. */}
          <p>
            {isTruncated
              ? 'None of the 20 requests that can be shown is waiting for a decision.'
              : 'Nobody is waiting to join this distributor.'}
          </p>
          {isTruncated && (
            <p className="text-sm mt-2">
              There may be more that are not listed.
            </p>
          )}
          <p className="text-sm mt-2">
            Requests appear here when someone asks to join from the app.
          </p>
        </div>
      );
    }

    return (
      <Table aria-label="Requests to join this distributor">
        <TableHeader>
          <TableColumn>User</TableColumn>
          <TableColumn>Contact</TableColumn>
          <TableColumn>Actions</TableColumn>
        </TableHeader>
        <TableBody>
          {requests.map((request) => {
            const name = displayName(request);
            const isProcessing = processingRequestId === request.id;

            return (
              <TableRow key={request.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={getInitials(name)}
                      size="sm"
                      color="warning"
                    />
                    <p className="font-semibold">{name}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {request.user?.email && (
                      <div className="flex items-center gap-1">
                        <FiMail size={12} />
                        {request.user.email}
                      </div>
                    )}
                    {request.user?.phone && (
                      <div className="flex items-center gap-1 mt-1">
                        <FiPhone size={12} />
                        {request.user.phone}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      color="success"
                      variant="flat"
                      isLoading={isProcessing}
                      isDisabled={processingRequestId !== null}
                      onPress={() =>
                        decideJoinRequest(request.id, 'approved', name)
                      }
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      color="danger"
                      variant="flat"
                      isLoading={isProcessing}
                      isDisabled={processingRequestId !== null}
                      onPress={() =>
                        decideJoinRequest(request.id, 'denied', name)
                      }
                    >
                      Decline
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h3 className="text-lg font-semibold">Requests to join</h3>
            <p className="text-sm text-default-500">
              Approving adds the person to this distributor as a sales rep.
            </p>
          </div>
          {requests.length > 0 && (
            <Chip size="sm" variant="flat" color="warning">
              {requests.length}
              {isTruncated ? '+' : ''} waiting
            </Chip>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {renderBody()}

        {/* A full page proves the cap was reached, not that anything sits
            behind it: a distributor with exactly 20 requests fills it too. */}
        {isTruncated && (
          <p className="mt-3 text-sm text-default-500">
            Only 20 requests can be shown. There may be more, waiting or not,
            that are not listed here.
          </p>
        )}
      </CardBody>
    </Card>
  );
};
